import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Select from 'react-select';

const SelectContainer = styled('div')`
  .react-select__control {
    min-height: 40px;
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
    border-radius: 4px;
    background-color: ${({ theme }) => theme.colors.surface};
    box-shadow: none;

    &:hover {
      border-color: ${({ theme }) => theme.colors.text};
    }

    &--is-focused {
      border-color: ${({ theme }) => theme.colors.text};
      box-shadow: none;
    }
  }

  /* react-select's own focus ring is killed by the box-shadow: none above, so
     give the control the same blue border as every other field. Written at the
     top level because the attribute sits on an ancestor of this element. Gated
     on keyboard nav because the focusable element react-select renders is an
     <input>, and :focus-visible matches those even for mouse clicks. */
  [data-nav-mode='keyboard'] & .react-select__control--is-focused {
    border-color: ${({ theme }) => theme.colors.hover};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.hover};
  }

  .react-select__value-container {
    padding: 2px 12px;
  }

  .react-select__single-value {
    font-family: 'Nunito Sans';
    font-size: 14px;
    color: ${({ theme }) => theme.colors.text};
  }

  .react-select__menu {
    border: none;
    border-radius: 4px;
    background-color: ${({ theme }) => theme.colors.surface};
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  }

  .react-select__option {
    font-family: 'Nunito Sans';
    font-size: 14px;
    color: ${({ theme }) => theme.colors.text};
    cursor: pointer;

    &--is-selected {
      background-color: ${({ theme }) => theme.colors.surfaceSecondary};
      color: ${({ theme }) => theme.colors.text};
    }

    &--is-focused,
    &:hover {
      background-color: ${({ theme }) => theme.colors.surfaceSecondary};
    }
  }
`;

/**
 * react-select 2.x opens on Space and the arrow keys but deliberately ignores
 * Enter while closed, so Enter is added here. It reads menuIsOpen back off the
 * component, which means open state has to be controlled.
 */
const CopyPlacementSelect = ({
  inputId, options, value, onChange,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleChange = (option) => {
    onChange(option);
    setIsMenuOpen(false);
  };

  const handleMenuOpen = () => setIsMenuOpen(true);
  const handleMenuClose = () => setIsMenuOpen(false);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isMenuOpen) {
      /* react-select checks defaultPrevented before running its own handler,
         so this cleanly takes over rather than racing it. */
      e.preventDefault();
      setIsMenuOpen(true);
    }
  };

  return (
    <SelectContainer>
      <Select
        className='react-select'
        classNamePrefix='react-select'
        inputId={inputId}
        isSearchable={false}
        menuIsOpen={isMenuOpen}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onMenuClose={handleMenuClose}
        onMenuOpen={handleMenuOpen}
        options={options}
        value={value}
      />
    </SelectContainer>
  );
};

CopyPlacementSelect.propTypes = {
  inputId: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
  value: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

CopyPlacementSelect.defaultProps = {
  inputId: undefined,
};

export default CopyPlacementSelect;
